import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Zap,
  Droplets,
  Flame,
  Wifi,
  Smartphone,
  SmartphoneNfc,
  Car,
  Building2,
  Tv,
  Landmark,
  Banknote,
  CreditCard,
  ShieldCheck,
  GraduationCap,
  Save,
  Layers,
} from "lucide-react";
import {
  getAdminMasterBbpsCategories,
  updateGlobalBbpsCategories,
} from "../../../services/bbpsAdminService";
import Button from "../../../components/ui/Button";

// Icon lookup map
const ICON_MAP = {
  Zap,
  Droplets,
  Flame,
  Wifi,
  Smartphone,
  SmartphoneNfc,
  Car,
  Building2,
  Tv,
  Landmark,
  Banknote,
  CreditCard,
  ShieldCheck,
  GraduationCap,
};

const SwitchToggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      width: 40,
      height: 22,
      borderRadius: 11,
      background: checked ? "#2563eb" : "#cbd5e1",
      border: "none",
      cursor: "pointer",
      position: "relative",
      padding: 0,
      transition: "background 0.2s ease",
      outline: "none",
      flexShrink: 0,
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#ffffff",
        position: "absolute",
        top: 2,
        left: checked ? 20 : 2,
        transition: "left 0.2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
    />
  </button>
);

export default function BbpsMasterSettingsTab() {
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTag, setSelectedTag] = useState("All");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getAdminMasterBbpsCategories();
      if (data?.success) {
        setCategories(data.categories || []);
        setSettings(data.globalSettings || {});
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load BBPS categories");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id) => {
    setSettings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleBulkToggle = (enable) => {
    setSettings((prev) => {
      const next = { ...prev };
      categories.forEach((c) => {
        if (selectedTag === "All" || c.tag === selectedTag) {
          next[c.id] = enable;
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateGlobalBbpsCategories(settings);
      if (res?.success) {
        toast.success("Global BBPS categories saved successfully!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save BBPS settings");
    } finally {
      setSaving(false);
    }
  };

  const tags = ["All", ...Array.from(new Set(categories.map((c) => c.tag).filter(Boolean)))];

  const filteredCategories = categories.filter((c) => {
    if (selectedTag !== "All" && c.tag !== selectedTag) return false;
    return true;
  });

  const activeCount = Object.values(settings).filter(Boolean).length;

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
        Loading BBPS configuration...
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header Banner */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ background: "#eff6ff", color: "#2563eb", padding: 6, borderRadius: 8, display: "flex" }}>
              <Layers size={20} />
            </div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              Bharat BillPay (BBPS) Master Categories
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b", maxWidth: 650 }}>
            Configure which NPCI utility categories are globally active. Disabling a category here completely disables it across all properties on the resident mobile app.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              padding: "6px 12px",
              background: "#f0fdf4",
              color: "#166534",
              border: "1px solid #bbf7d0",
              borderRadius: 20,
            }}
          >
            {activeCount} of {categories.length} Categories Enabled
          </div>

          <Button
            onClick={handleSave}
            loading={saving}
            icon={<Save size={16} />}
            style={{ background: "var(--color-dark-bg, #27306f)", color: "#fff" }}
          >
            Save Global Master
          </Button>
        </div>
      </div>

      {/* Filter and Bulk Action Toolbar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {/* Tag Tabs */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setSelectedTag(t)}
              style={{
                padding: "6px 12px",
                borderRadius: 20,
                fontSize: 12.5,
                fontWeight: 600,
                border: "1px solid",
                borderColor: selectedTag === t ? "#2563eb" : "#cbd5e1",
                background: selectedTag === t ? "#eff6ff" : "#ffffff",
                color: selectedTag === t ? "#1d4ed8" : "#475569",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Bulk Toggle Buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => handleBulkToggle(true)}
            style={{
              padding: "5px 10px",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              background: "#f1f5f9",
              border: "1px solid #cbd5e1",
              color: "#334155",
              cursor: "pointer",
            }}
          >
            Enable Filtered
          </button>
          <button
            type="button"
            onClick={() => handleBulkToggle(false)}
            style={{
              padding: "5px 10px",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              background: "#f1f5f9",
              border: "1px solid #cbd5e1",
              color: "#334155",
              cursor: "pointer",
            }}
          >
            Disable Filtered
          </button>
        </div>
      </div>

      {/* Category Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        {filteredCategories.map((cat) => {
          const isEnabled = Boolean(settings[cat.id]);
          const IconComponent = ICON_MAP[cat.icon] || Zap;

          return (
            <div
              key={cat.id}
              style={{
                background: isEnabled ? "#ffffff" : "#f8fafc",
                border: "1px solid",
                borderColor: isEnabled ? "#e2e8f0" : "#e2e8f0",
                borderRadius: 12,
                padding: 16,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                transition: "all 0.2s ease",
                boxShadow: isEnabled ? "0 1px 3px rgba(0,0,0,0.04)" : "none",
                opacity: isEnabled ? 1 : 0.75,
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: `${cat.color || "#2563eb"}15`,
                    color: cat.color || "#2563eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconComponent size={20} />
                </div>

                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                      {cat.title}
                    </span>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: "#f1f5f9",
                        color: "#475569",
                      }}
                    >
                      {cat.tag}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: "#64748b", lineHeight: "1.4" }}>
                    {cat.description}
                  </p>
                </div>
              </div>

              <SwitchToggle
                checked={isEnabled}
                onChange={() => handleToggle(cat.id)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
